import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface AudioLevelMeterProps {
    /** The MediaStream to analyse. Pass null/undefined when muted or no stream. */
    stream: MediaStream | null | undefined;
    /** Whether the participant is currently muted (hides the meter). */
    muted?: boolean;
    /** Number of bars to render (default 4). */
    bars?: number;
    /** Additional class names for the outer container. */
    className?: string;
}

/**
 * Small audio-level meter that shows animated bars next to a participant
 * tile — mirrors Teams' "speaking" indicator.
 *
 * Implementation uses the Web Audio API:
 *   MediaStream → MediaStreamAudioSourceNode → AnalyserNode → getByteFrequencyData()
 *
 * A shared AudioContext is reused across instances via a module-level
 * singleton so we don't hit the browser's 6-context limit in gallery view.
 *
 * The component renders nothing when `muted` is true or `stream` is null
 * so callers don't need to gate on it themselves.
 */
export function AudioLevelMeter({ stream, muted = false, bars = 4, className }: AudioLevelMeterProps) {
    const [levels, setLevels] = useState<number[]>(Array(bars).fill(0));
    const rafRef = useRef<number | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

    useEffect(() => {
        if (!stream || muted) {
            setLevels(Array(bars).fill(0));
            return;
        }

        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length === 0) return;

        let cancelled = false;
        let ctx: AudioContext | null = null;

        const setup = () => {
            try {
                ctx = getSharedAudioContext();
                const source = ctx.createMediaStreamSource(stream);
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 256;
                analyser.smoothingTimeConstant = 0.6;
                source.connect(analyser);
                analyserRef.current = analyser;
                sourceRef.current = source;

                const dataArray = new Uint8Array(analyser.frequencyBinCount);

                const tick = () => {
                    if (cancelled) return;
                    analyser.getByteFrequencyData(dataArray);

                    // Slice the frequency data into `bars` buckets and map
                    // each to a 0–1 normalised level. We only care about the
                    // lower 2/3 of the spectrum (voice sits roughly 85–3 kHz).
                    const voiceBins = Math.floor(dataArray.length * 0.67);
                    const bucketSize = Math.floor(voiceBins / bars);
                    const newLevels: number[] = [];
                    for (let i = 0; i < bars; i++) {
                        let sum = 0;
                        for (let j = 0; j < bucketSize; j++) {
                            sum += dataArray[i * bucketSize + j] ?? 0;
                        }
                        newLevels.push(Math.min(1, (sum / bucketSize) / 200));
                    }
                    setLevels(newLevels);
                    rafRef.current = requestAnimationFrame(tick);
                };

                rafRef.current = requestAnimationFrame(tick);
            } catch {
                // AudioContext may be blocked by browser autoplay policy; degrade silently.
            }
        };

        setup();

        return () => {
            cancelled = true;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            try { sourceRef.current?.disconnect(); } catch { /* ignore */ }
            analyserRef.current = null;
            sourceRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stream, muted, bars]);

    if (muted || !stream) return null;

    return (
        <div
            className={cn("flex items-end gap-[1.5px]", className)}
            aria-hidden="true"
            role="presentation"
        >
            {levels.map((level, i) => (
                <span
                    key={i}
                    className="inline-block w-[3px] rounded-[1px] bg-current transition-all duration-75"
                    style={{ height: `${Math.max(3, Math.round(level * 14))}px` }}
                />
            ))}
        </div>
    );
}

// ── Shared AudioContext singleton ──────────────────────────────────────────
// Browsers cap AudioContexts per page (~6 in Chrome). Gallery calls can
// have 5+ remote tiles; sharing one context keeps us safely within limits.

let sharedCtx: AudioContext | null = null;

export function getSharedAudioContext(): AudioContext {
    if (!sharedCtx || sharedCtx.state === "closed") {
        sharedCtx = new AudioContext();
    }
    if (sharedCtx.state === "suspended") {
        // Resume on next user gesture — browsers may auto-suspend.
        void sharedCtx.resume();
    }
    return sharedCtx;
}
